// ============================================
// FAQ 留言详情（/faq/questions/:questionId）
// ============================================

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Activity, AlertCircle, ArrowLeft, BookOpen, CheckCircle2, Clock3, HelpCircle,
  LifeBuoy, Loader2, MessageCircle, MessageSquareReply, Pencil, Save, Send,
  ShieldCheck, UserCircle, X, XCircle,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { useUserStore } from '../stores/user-store';
import FaqAuthModal from './FaqAuthModal';
import FaqDiscussionContent from './FaqDiscussionContent';
import FaqQuestionModal from './FaqQuestionModal';
import FaqRichEditor from './FaqRichEditor';
import { useFaqQuestionStore } from './faq-question-store';
import { uploadFaqImage } from './faq-service';
import { EMPTY_TIPTAP_DOC, ensureTiptapContent, richTextHasContent } from './faq-rich-content';
import { useFaqSpace } from './FaqSpaceContext';
import type { FaqQuestionReply, FaqQuestionStatus } from './types';

const statusConfig: Record<FaqQuestionStatus, { label: string; icon: typeof Clock3; className: string }> = {
  open: { label: '待回复', icon: Clock3, className: 'bg-amber-50 text-amber-600 border-amber-100' },
  answered: { label: '已回复', icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  closed: { label: '已关闭', icon: XCircle, className: 'bg-slate-100 text-slate-500 border-slate-200' },
};

function fmtTime(value: string | null) {
  if (!value) return '';
  return new Date(value).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getInitial(reply: FaqQuestionReply) {
  return (reply.author?.name || reply.author?.email || 'U')[0].toUpperCase();
}

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

export default function FaqQuestionDetailPage() {
  const { questionId } = useParams<{ questionId: string }>();
  const navigate = useNavigate();
  const { space, path, adminPath } = useFaqSpace();
  const { user } = useAuthStore();
  const { profile } = useUserStore();
  const {
    currentQuestion,
    detailLoading,
    saving,
    error,
    fetchQuestionDetail,
    addReply,
    updateReply,
    clearCurrentQuestion,
  } = useFaqQuestionStore();
  const [authOpen, setAuthOpen] = useState(false);
  const [editQuestionOpen, setEditQuestionOpen] = useState(false);
  const [body, setBody] = useState(EMPTY_TIPTAP_DOC);
  const [bodyEditorKey, setBodyEditorKey] = useState(0);
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editingReplyBody, setEditingReplyBody] = useState(EMPTY_TIPTAP_DOC);
  const [editingReplyKey, setEditingReplyKey] = useState(0);
  const [submitError, setSubmitError] = useState('');
  const uploadImage = (file: File) => uploadFaqImage(file, space.slug);

  useEffect(() => {
    if (user && questionId) {
      fetchQuestionDetail(questionId);
      setBody(EMPTY_TIPTAP_DOC);
      setBodyEditorKey((key) => key + 1);
      setEditingReplyId(null);
      setSubmitError('');
    }
    return () => clearCurrentQuestion();
  }, [user, questionId, fetchQuestionDetail, clearCurrentQuestion]);

  async function handleSupplement() {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    if (!currentQuestion || !richTextHasContent(body)) return;
    setSubmitError('');
    try {
      await addReply({
        questionId: currentQuestion.id,
        body,
        bodyFormat: 'tiptap',
        isAdminReply: false,
      });
      setBody(EMPTY_TIPTAP_DOC);
      setBodyEditorKey((key) => key + 1);
    } catch (error) {
      setSubmitError(getErrorMessage(error, '提交失败'));
    }
  }

  function startEditReply(reply: FaqQuestionReply) {
    setEditingReplyId(reply.id);
    setEditingReplyBody(ensureTiptapContent(reply.body, reply.body_format));
    setEditingReplyKey((key) => key + 1);
    setSubmitError('');
  }

  async function handleSaveReply(reply: FaqQuestionReply) {
    if (!currentQuestion || !richTextHasContent(editingReplyBody)) return;
    setSubmitError('');
    try {
      await updateReply(reply.id, {
        questionId: currentQuestion.id,
        body: editingReplyBody,
        bodyFormat: 'tiptap',
      });
      setEditingReplyId(null);
      setEditingReplyBody(EMPTY_TIPTAP_DOC);
    } catch (error) {
      setSubmitError(getErrorMessage(error, '保存失败'));
    }
  }

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <UserCircle size={44} className="mx-auto mb-4 text-sky-500" />
          <h1 className="text-lg font-bold text-slate-800">请先登录</h1>
          <p className="mt-2 text-sm text-slate-500">登录后可以查看留言详情和管理员回复。</p>
          <button
            onClick={() => setAuthOpen(true)}
            className="mt-5 rounded-xl bg-sky-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-600"
          >
            登录 / 注册
          </button>
        </div>
        <FaqAuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
      </div>
    );
  }

  if (detailLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 size={26} className="animate-spin text-sky-500" />
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="flex h-full items-center justify-center px-4">
        <div className="text-center">
          <HelpCircle size={44} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-400">{error || '未找到该留言'}</p>
          <button onClick={() => navigate(path('questions'))} className="mt-3 text-sm font-medium text-sky-500 hover:underline">
            返回留言列表
          </button>
        </div>
      </div>
    );
  }

  const config = statusConfig[currentQuestion.status];
  const Icon = config.icon;
  const canSupplement = currentQuestion.created_by === user.id && currentQuestion.status !== 'closed';
  const canEditQuestion = currentQuestion.created_by === user.id && currentQuestion.status !== 'closed';
  const canManageFaq = !!profile?.is_faq_admin || profile?.role === 'super_admin';
  const adminReplyHref = `${adminPath('questions')}?questionId=${encodeURIComponent(currentQuestion.id)}&section=reply`;
  const adminReplyCount = currentQuestion.replies.filter((reply) => reply.is_admin_reply).length;
  const participantCount = new Set([
    currentQuestion.created_by,
    ...currentQuestion.replies.map((reply) => reply.author_id),
  ].filter(Boolean)).size;

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-7">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <button
            onClick={() => navigate(path('questions'))}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 transition hover:text-sky-500"
          >
            <ArrowLeft size={14} />
            返回留言中心
          </button>
          <Link
            to={path('knowledge')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-600"
          >
            <BookOpen size={13} />
            知识问答
          </Link>
          <Link
            to={path()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-600"
          >
            <LifeBuoy size={13} />
            支持首页
          </Link>
        </div>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
          <main className="min-w-0">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-5">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-semibold ${config.className}`}>
                <Icon size={13} />
                {config.label}
              </span>
              <span className="text-xs text-slate-400">提交于 {fmtTime(currentQuestion.created_at)}</span>
              {canEditQuestion && (
                <button
                  onClick={() => setEditQuestionOpen(true)}
                  className="ml-auto inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500 transition hover:bg-sky-50 hover:text-sky-600"
                >
                  <Pencil size={12} />
                  编辑留言
                </button>
              )}
            </div>
            <h1 className="text-xl font-bold leading-snug text-slate-800 sm:text-2xl">{currentQuestion.title}</h1>
            <FaqDiscussionContent
              content={currentQuestion.content}
              format={currentQuestion.content_format}
              className="mt-2"
            />

            {currentQuestion.related_faq && (
              <Link
                to={path(currentQuestion.related_faq.id)}
                className="mt-4 block rounded-xl border border-sky-100 bg-sky-50/60 px-4 py-3 transition hover:border-sky-200 hover:bg-sky-50"
              >
                <p className="text-[11px] font-semibold uppercase tracking-wider text-sky-500">原始关联 FAQ</p>
                <p className="mt-1 text-sm font-semibold text-slate-700">{currentQuestion.related_faq.question}</p>
              </Link>
            )}
          </div>

          <div className="space-y-4 px-5 py-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold text-slate-700">沟通记录</h2>
                <p className="mt-1 text-xs text-slate-400">员工补充和管理员回复会按时间保留在这里。</p>
              </div>
              <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
                {currentQuestion.replies.length} 条回复
              </span>
            </div>

            {currentQuestion.replies.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 py-8 text-center">
                <Clock3 size={30} className="mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-slate-400">管理员还没有回复</p>
              </div>
            )}

            {currentQuestion.replies.map((reply) => {
              const canEditReply = reply.author_id === user.id && !reply.is_admin_reply && currentQuestion.status !== 'closed';
              const isEditingReply = editingReplyId === reply.id;

              return (
              <div key={reply.id} className={`rounded-2xl border px-4 py-4 ${
                reply.is_admin_reply ? 'border-emerald-100 bg-emerald-50/40' : 'border-slate-200 bg-white'
              }`}>
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
                      reply.is_admin_reply ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {getInitial(reply)}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-slate-700">
                        {reply.author?.name || reply.author?.email || '用户'}
                        {reply.is_admin_reply && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-600">管理员</span>}
                      </p>
                      <p className="text-[11px] text-slate-400">{fmtTime(reply.created_at)}</p>
                    </div>
                  </div>
                  {canEditReply && !isEditingReply && (
                    <button
                      onClick={() => startEditReply(reply)}
                      className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500 transition hover:bg-sky-50 hover:text-sky-600"
                    >
                      <Pencil size={12} />
                      编辑
                    </button>
                  )}
                </div>
                {isEditingReply ? (
                  <div>
                    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3">
                      <FaqRichEditor
                        key={editingReplyKey}
                        content={editingReplyBody}
                        onChange={setEditingReplyBody}
                        placeholder="编辑补充内容，可上传截图"
                        uploadImage={uploadImage}
                        minHeight="130px"
                      />
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        onClick={() => setEditingReplyId(null)}
                        className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-500 transition hover:bg-slate-200"
                      >
                        <X size={13} />
                        取消
                      </button>
                      <button
                        onClick={() => handleSaveReply(reply)}
                        disabled={saving || !richTextHasContent(editingReplyBody)}
                        className="inline-flex items-center gap-1 rounded-lg bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-sky-600 disabled:opacity-60"
                      >
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        保存
                      </button>
                    </div>
                  </div>
                ) : (
                  <FaqDiscussionContent content={reply.body} format={reply.body_format} />
                )}

                {reply.links && reply.links.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-sky-600">
                      <MessageSquareReply size={13} />
                      管理员附上的 FAQ
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {reply.links.map((link) => link.faq && (
                        <Link
                          key={link.id}
                          to={path(link.faq.id)}
                          className="rounded-xl border border-sky-100 bg-white px-3 py-3 text-sm font-semibold text-slate-700 transition hover:border-sky-200 hover:text-sky-600"
                        >
                          {link.faq.question}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>

        {canSupplement && (
          <div id="faq-question-supplement-editor" className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm scroll-mt-5">
            <h2 className="text-sm font-bold text-slate-700">补充说明</h2>
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 transition focus-within:border-sky-300 focus-within:ring-2 focus-within:ring-sky-100">
              <FaqRichEditor
                key={bodyEditorKey}
                content={body}
                onChange={setBody}
                placeholder="如果还有补充信息，可以继续留言给管理员，可直接粘贴或上传截图"
                  uploadImage={uploadImage}
                minHeight="130px"
              />
            </div>
            {submitError && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-red-500">
                <AlertCircle size={13} />
                {submitError}
              </div>
            )}
            <div className="mt-3 flex justify-end">
              <button
                onClick={handleSupplement}
                disabled={saving || !richTextHasContent(body)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-sky-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-600 disabled:opacity-60"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                提交补充
              </button>
            </div>
          </div>
        )}
          </main>

          <aside className="space-y-4">
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <Icon size={16} className="text-slate-500" />
                <h2 className="text-sm font-bold text-slate-800">留言状态</h2>
              </div>
              <div className="space-y-3">
                <div className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-semibold ${config.className}`}>
                  <Icon size={13} />
                  {config.label}
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-xl bg-slate-50 px-2 py-2">
                    <p className="text-base font-bold text-slate-800">{currentQuestion.replies.length}</p>
                    <p className="mt-0.5 text-[10px] text-slate-400">回复</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 px-2 py-2">
                    <p className="text-base font-bold text-slate-800">{participantCount}</p>
                    <p className="mt-0.5 text-[10px] text-slate-400">参与</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 px-2 py-2">
                    <p className="text-base font-bold text-slate-800">{adminReplyCount}</p>
                    <p className="mt-0.5 text-[10px] text-slate-400">官方</p>
                  </div>
                </div>
                <div className="space-y-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
                  <p className="flex items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-1.5"><UserCircle size={13} /> 提问人</span>
                    <span className="truncate font-medium text-slate-700">{currentQuestion.author?.name || currentQuestion.author?.email || '员工'}</span>
                  </p>
                  <p className="flex items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-1.5"><Clock3 size={13} /> 创建时间</span>
                    <span className="font-medium text-slate-700">{fmtTime(currentQuestion.created_at)}</span>
                  </p>
                  <p className="flex items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-1.5"><Activity size={13} /> 最近活跃</span>
                    <span className="font-medium text-slate-700">{fmtTime(currentQuestion.last_reply_at || currentQuestion.updated_at)}</span>
                  </p>
                </div>
              </div>
            </section>

            {currentQuestion.related_faq && (
              <section className="rounded-2xl border border-sky-100 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                  <BookOpen size={16} className="text-sky-500" />
                  <h2 className="text-sm font-bold text-slate-800">关联知识</h2>
                </div>
                <Link
                  to={path(currentQuestion.related_faq.id)}
                  className="block rounded-xl bg-sky-50 px-3 py-3 transition hover:bg-sky-100"
                >
                  <p className="line-clamp-3 text-sm font-semibold text-slate-700">{currentQuestion.related_faq.question}</p>
                </Link>
              </section>
            )}

            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <ShieldCheck size={16} className="text-emerald-500" />
                <h2 className="text-sm font-bold text-slate-800">下一步</h2>
              </div>
              <div className="space-y-3 text-xs leading-5 text-slate-500">
                {currentQuestion.status === 'open' && (
                  <p>管理员还未回复。你可以继续补充截图、系统页面或期望处理结果。</p>
                )}
                {currentQuestion.status === 'answered' && (
                  <p>管理员已回复。若问题未解决，可以继续在下方补充说明。</p>
                )}
                {currentQuestion.status === 'closed' && (
                  <p>该留言已关闭，内容仍可作为历史讨论查看。</p>
                )}
                <Link to={path('knowledge')} className="inline-flex items-center gap-1 font-semibold text-sky-600 hover:text-sky-700">
                  查看知识问答
                  <BookOpen size={12} />
                </Link>
              </div>
            </section>

            {canManageFaq && (
              <section className="rounded-2xl border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-white p-4 shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-sky-500 text-white shadow-sm shadow-sky-200">
                    <MessageSquareReply size={16} />
                  </span>
                  <div>
                    <h2 className="text-sm font-bold text-slate-800">管理员处理</h2>
                    <p className="mt-0.5 text-xs text-slate-400">进入管理台回复并通知提问人</p>
                  </div>
                </div>
                <Link
                  to={adminReplyHref}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-sky-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-600"
                >
                  <MessageSquareReply size={15} />
                  管理员回复
                </Link>
              </section>
            )}

            {canSupplement && (
              <button
                onClick={() => document.getElementById('faq-question-supplement-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="flex w-full items-center justify-between rounded-2xl bg-sky-500 px-4 py-3 text-left text-white shadow-sm transition hover:bg-sky-600"
              >
                <span>
                  <span className="block text-sm font-bold">继续补充</span>
                  <span className="mt-0.5 block text-xs text-sky-100">添加截图或更多上下文</span>
                </span>
                <MessageCircle size={18} />
              </button>
            )}
          </aside>
        </div>
      </div>
      <FaqQuestionModal
        isOpen={editQuestionOpen}
        onClose={() => setEditQuestionOpen(false)}
        editingQuestion={currentQuestion}
        onSaved={(question) => fetchQuestionDetail(question.id)}
      />
    </div>
  );
}
